import {
  type CSSProperties,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";

const APP_URL = import.meta.env.DEV
  ? "http://localhost:5174"
  : "https://app.penzr.com";
const PRO_INTENT_URL = `${APP_URL}?intent=pro`;

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

type IconProps = {
  size?: number;
  color?: string;
};

type TextProps = {
  children: ReactNode;
};

function useTyped(
  text: string,
  {
    speed = 46,
    startDelay = 600,
  }: {
    speed?: number;
    startDelay?: number;
  } = {},
) {
  const [n, setN] = useState(() => (prefersReducedMotion() ? text.length : 0));

  useEffect(() => {
    const reduce = prefersReducedMotion();
    if (reduce) return undefined;

    let intervalId: number | undefined;
    const timeoutId = window.setTimeout(() => {
      intervalId = window.setInterval(() => {
        setN((current) => {
          const next = current + 1;
          if (next >= text.length && intervalId !== undefined) {
            window.clearInterval(intervalId);
          }
          return Math.min(next, text.length);
        });
      }, speed);
    }, startDelay);

    return () => {
      window.clearTimeout(timeoutId);
      if (intervalId !== undefined) window.clearInterval(intervalId);
    };
  }, [speed, startDelay, text]);

  return [text.slice(0, n), n >= text.length] as const;
}

function useInViewOnce(cb: () => void) {
  const ref = useRef<HTMLDivElement | null>(null);
  const cbRef = useRef(cb);

  useEffect(() => {
    cbRef.current = cb;
  }, [cb]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const reduce = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reduce) {
      cbRef.current();
      return undefined;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            cbRef.current();
            io.unobserve(el);
          }
        });
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return ref;
}

function TypedLine({
  text,
  sliceFrom = 0,
  speed = 46,
  startDelay = 600,
  caretStyle,
}: {
  text: string;
  sliceFrom?: number;
  speed?: number;
  startDelay?: number;
  caretStyle?: CSSProperties;
}) {
  const [shown, done] = useTyped(text, { speed, startDelay });

  return (
    <>
      {shown.slice(sliceFrom)}
      {!done && <span className="caret" style={caretStyle} />}
    </>
  );
}

function Wordmark({ size = 23, color = "var(--ink)" }: IconProps) {
  return (
    <a
      href="/"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        color,
        textDecoration: "none",
      }}
    >
      <PenNib size={size * 0.92} color="var(--oxblood)" />
      <span
        style={{
          color,
          fontFamily: "var(--serif)",
          fontSize: size,
          fontWeight: 600,
          letterSpacing: ".02em",
        }}
      >
        Penzr
      </span>
    </a>
  );
}

function svgWrap(size: number, children: ReactNode, vb = 24) {
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${vb} ${vb}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

function PenNib({ size = 22, color = "currentColor" }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 19 L13.5 6.5 a3 3 0 0 1 4 4 L5 19Z" />
      <path d="M5 19 l3.4 -1.1" />
      <circle cx="12.4" cy="11.6" r="1.5" />
    </svg>
  );
}

function IconBinder({ size = 22 }: IconProps) {
  return svgWrap(
    size,
    <>
      <rect x="4" y="3.5" width="16" height="17" rx="1.5" />
      <path d="M9 3.5v17" />
      <path d="M12 8h5M12 12h5M12 16h3" />
      <path d="M6.4 7h0.01M6.4 11h0.01M6.4 15h0.01" />
    </>,
  );
}

function IconPage({ size = 22 }: IconProps) {
  return svgWrap(
    size,
    <>
      <path d="M7 4h7l4 4v12H7z" />
      <path d="M14 4v4h4" />
      <path d="M10 12h5M10 15.5h5" />
    </>,
  );
}

function IconQuill({ size = 22 }: IconProps) {
  return svgWrap(
    size,
    <>
      <path d="M19 5c-6 1-9 4-11 9l-2 5 5-2c5-2 8-5 9-11z" />
      <path d="M6 19c3-5 6-8 11-10" />
    </>,
  );
}

function IconSearch({ size = 22 }: IconProps) {
  return svgWrap(
    size,
    <>
      <circle cx="10.5" cy="10.5" r="6" />
      <path d="M15 15l4.5 4.5" />
      <path d="M8 10.5h5" opacity=".5" />
    </>,
  );
}

function IconHistory({ size = 22 }: IconProps) {
  return svgWrap(
    size,
    <>
      <path d="M4 11a8 8 0 1 1 1.6 4.8" />
      <path d="M4 16v-4h4" />
      <path d="M12 8v4l3 2" />
    </>,
  );
}

function IconShield({ size = 22 }: IconProps) {
  return svgWrap(
    size,
    <>
      <path d="M12 3.5l7 2.5v5c0 5-3.4 8-7 9.5-3.6-1.5-7-4.5-7-9.5V6z" />
      <path d="M9 12l2 2 4-4.5" />
    </>,
  );
}

function IconExport({ size = 22 }: IconProps) {
  return svgWrap(
    size,
    <>
      <path d="M7 4h7l4 4v12H7z" />
      <path d="M14 4v4h4" />
      <path d="M12 17v-6" />
      <path d="M9.5 13.5L12 11l2.5 2.5" />
    </>,
  );
}

function IconCheck({ size = 18 }: IconProps) {
  return svgWrap(size, <path d="M5 12.5l4 4 10-10.5" />);
}

function IconArrow({ size = 18 }: IconProps) {
  return svgWrap(
    size,
    <>
      <path d="M5 12h14" />
      <path d="M13 6l6 6-6 6" />
    </>,
  );
}

function Avatar({
  initials,
  tint,
  size = 30,
}: {
  initials: string;
  tint: string;
  size?: number;
}) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "none",
        width: size,
        height: size,
        borderRadius: size,
        background: tint,
        color: "#fff",
        boxShadow: "0 0 0 2px var(--paper)",
        fontFamily: "var(--sans)",
        fontSize: size * 0.36,
        fontWeight: 600,
      }}
    >
      {initials}
    </span>
  );
}

function Nav() {
  const [solid, setSolid] = useState(false);

  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 36);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 50,
        borderBottom: solid ? "1px solid var(--rule)" : "1px solid transparent",
        background: solid ? "rgba(241,233,218,.86)" : "transparent",
        backdropFilter: solid ? "blur(8px)" : "none",
        transition: "background .3s, border-color .3s",
      }}
    >
      <div
        className="wrap"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 70,
        }}
      >
        <Wordmark />
        <nav
          className="nav-desktop"
          style={{ display: "flex", alignItems: "center", gap: 28 }}
        >
          <a className="nav-link" href="#binder">
            Features
          </a>
          <a className="nav-link" href="#assistant">
            Assistant
          </a>
          <a className="nav-link" href="#pricing">
            Pricing
          </a>
          <a className="btn btn-ink btn-sm" href={APP_URL}>
            Start writing
          </a>
        </nav>
        <a
          className="btn btn-ink btn-sm nav-mobile"
          href={APP_URL}
          style={{ display: "none" }}
        >
          Start writing
        </a>
      </div>
    </header>
  );
}

function ChapterHead({
  no,
  name,
  align = "left",
}: {
  no: string;
  name: string;
  align?: "left" | "center";
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: align === "center" ? "center" : "flex-start",
        gap: 14,
        marginBottom: 22,
      }}
    >
      <span className="chapno">{no}</span>
      <span style={{ width: 28, height: 1, background: "var(--rule)" }} />
      <span className="kicker">{name}</span>
    </div>
  );
}

function Hero() {
  const line = "Marlowe had not slept, and the harbour already knew it.";

  return (
    <section id="top" style={{ position: "relative", paddingTop: 18 }}>
      <div className="wrap">
        <div className="hero-grid">
          <div style={{ paddingTop: "clamp(24px,5vw,64px)" }}>
            <div className="kicker" style={{ marginBottom: 24 }}>
              For serious writers
            </div>
            <h1 className="display" style={{ marginBottom: 26 }}>
              Write your
              <br />
              next story.
            </h1>
            <p className="lede" style={{ maxWidth: 480, marginBottom: 34 }}>
              Your story is more than a manuscript. It is scenes, notes,
              research, characters, revisions, and ideas you are still trying to
              capture.
              <br />
              <br />
              Penzr keeps it all together in one calm writing space, so you can
              focus on what matters: writing your story.
            </p>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 22,
                flexWrap: "wrap",
              }}
            >
              <a className="btn btn-ink" href={APP_URL}>
                Start writing — it&apos;s free <IconArrow size={17} />
              </a>
              <a className="btn btn-ghost" href="#binder">
                See how it works
              </a>
            </div>
            <div
              style={{
                marginTop: 28,
                color: "var(--faint)",
                fontSize: 17,
                fontStyle: "italic",
              }}
            >
              Free for your first project. Works in your browser. Nothing to
              install.
            </div>
          </div>

          <div className="hero-plate-wrap">
            <div
              className="plate"
              style={{
                padding: "clamp(26px,3vw,40px) clamp(24px,3vw,46px)",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: -7,
                  top: 18,
                  bottom: 22,
                  width: 6,
                  borderRadius: 1,
                  background: "linear-gradient(#3a2f24,#2a221a)",
                }}
              />
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  color: "var(--faint)",
                  fontFamily: "var(--sans)",
                  fontSize: 11.5,
                  letterSpacing: ".2em",
                  textTransform: "uppercase",
                }}
              >
                <span>The Salt Year</span>
                <span>Chapter One</span>
              </div>
              <hr className="rule-soft" style={{ margin: "16px 0 28px" }} />
              <p
                style={{
                  minHeight: "3.2em",
                  margin: 0,
                  fontSize: "clamp(20px,2.3vw,25px)",
                  lineHeight: 1.62,
                }}
              >
                <span
                  style={{
                    float: "left",
                    marginTop: 6,
                    paddingRight: 12,
                    color: "var(--oxblood)",
                    fontSize: 68,
                    fontWeight: 500,
                    lineHeight: 0.78,
                  }}
                >
                  {line[0]}
                </span>
                <TypedLine
                  text={line}
                  sliceFrom={1}
                  speed={44}
                  startDelay={850}
                />
              </p>
              <p
                style={{
                  marginTop: 18,
                  color: "rgba(34,29,23,.20)",
                  fontSize: "clamp(20px,2.3vw,25px)",
                  lineHeight: 1.62,
                }}
              >
                She had counted the bells from the cathedral twice, and both
                times lost the number to the wind coming off the water.
              </p>
            </div>
            <div
              className="marginalia"
              style={{
                position: "absolute",
                right: -14,
                bottom: 30,
                width: 158,
                transform: "rotate(2.6deg)",
              }}
            >
              move this beat to ch. 3?
            </div>
          </div>
        </div>
        <hr className="rule" style={{ marginTop: "clamp(56px,7vw,96px)" }} />
      </div>
    </section>
  );
}

function BinderRow({
  depth = 0,
  icon,
  label,
  count,
  active = false,
  folder = false,
  open = false,
}: {
  depth?: number;
  icon: ReactNode;
  label: string;
  count?: string;
  active?: boolean;
  folder?: boolean;
  open?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        padding: "7px 12px",
        paddingLeft: 12 + depth * 20,
        borderRadius: 3,
        background: active ? "rgba(138,59,46,.10)" : "transparent",
        color: active ? "var(--oxblood)" : "var(--ink-soft)",
        fontFamily: "var(--sans)",
        fontSize: 14.5,
      }}
    >
      {folder ? (
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          style={{
            flex: "none",
            opacity: 0.7,
            transform: open ? "rotate(90deg)" : "none",
            transition: "transform .2s",
          }}
          aria-hidden="true"
        >
          <path
            d="M9 6l6 6-6 6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <span style={{ width: 13, flex: "none" }} />
      )}
      <span style={{ display: "inline-flex", opacity: 0.75 }}>{icon}</span>
      <span style={{ flex: 1, fontWeight: active ? 600 : 500 }}>{label}</span>
      {count != null && (
        <span
          style={{
            color: "var(--faint)",
            fontSize: 12,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {count}
        </span>
      )}
    </div>
  );
}

function BinderPlate() {
  return (
    <div className="plate" style={{ maxWidth: 460, padding: 16 }}>
      <div
        style={{
          padding: "6px 10px 12px",
          color: "var(--faint)",
          fontFamily: "var(--sans)",
          fontSize: 11.5,
          letterSpacing: ".2em",
          textTransform: "uppercase",
        }}
      >
        Binder · The Salt Year
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <BinderRow
          folder
          open
          icon={<IconBinder size={15} />}
          label="Manuscript"
        />
        <BinderRow
          depth={1}
          icon={<IconPage size={14} />}
          label="Front Matter"
        />
        <BinderRow
          depth={1}
          folder
          open
          icon={<IconBinder size={15} />}
          label="Part One — The Harbour"
        />
        <BinderRow
          depth={2}
          icon={<IconPage size={14} />}
          label="1 · Salt and Smoke"
          count="2,140"
          active
        />
        <BinderRow
          depth={2}
          icon={<IconPage size={14} />}
          label="2 · The Cathedral Bells"
          count="1,884"
        />
        <BinderRow
          depth={2}
          icon={<IconPage size={14} />}
          label="3 · What the Tide Left"
          count="970"
        />
        <BinderRow
          depth={1}
          folder
          icon={<IconBinder size={15} />}
          label="Part Two — Inland"
        />
        <hr className="rule-soft" style={{ margin: "8px 6px" }} />
        <BinderRow
          folder
          open
          icon={<IconQuill size={15} />}
          label="Research"
        />
        <BinderRow
          depth={1}
          icon={<IconPage size={14} />}
          label="Tide tables, 1911"
        />
        <BinderRow
          depth={1}
          icon={<IconPage size={14} />}
          label="Characters & names"
          count="9"
        />
        <BinderRow
          depth={1}
          icon={<IconPage size={14} />}
          label="Scraps (not yet scenes)"
          count="14"
        />
      </div>
    </div>
  );
}

function Binder() {
  return (
    <section id="binder" style={{ padding: "clamp(64px,9vw,118px) 0" }}>
      <div className="wrap">
        <div className="split">
          <div className="order-plate">
            <BinderPlate />
          </div>
          <div className="order-copy">
            <ChapterHead no="Chapter I" name="The Binder" />
            <h2 className="h-sec" style={{ marginBottom: 22 }}>
              Your whole story,
              <br />
              in one place.
            </h2>
            <p className="prose dropcap" style={{ marginBottom: 18 }}>
              Manuscript pages, research, character notes, the half-scene you
              scribbled at breakfast — the binder keeps every part of your story
              close at hand. Move a chapter, gather loose notes, tuck away the
              pieces you&apos;re not ready for yet.
            </p>
            <p className="prose" style={{ color: "var(--muted)" }}>
              The shape of the story stays in view, so you can disappear into a
              single scene without losing the thread of the whole thing.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function FocusPlate() {
  const [wordCount, setWordCount] = useState(1284);
  const ref = useInViewOnce(() => {
    let next = 1284;
    const intervalId = window.setInterval(() => {
      next += 1;
      setWordCount(next);
      if (next >= 1342) window.clearInterval(intervalId);
    }, 90);
  });

  return (
    <div
      ref={ref}
      className="plate"
      style={{
        maxWidth: 520,
        padding: "clamp(24px,3vw,38px) clamp(26px,3.4vw,52px)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          color: "var(--faint)",
          fontFamily: "var(--sans)",
          fontSize: 11.5,
          letterSpacing: ".2em",
          textTransform: "uppercase",
        }}
      >
        <span>Focus mode</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: 6,
              background: "var(--saved)",
            }}
          />
          Saved
        </span>
      </div>
      <hr className="rule-soft" style={{ margin: "16px 0 26px" }} />
      <p
        style={{
          margin: 0,
          fontSize: "clamp(19px,2.1vw,23px)",
          lineHeight: 1.66,
        }}
      >
        The road bent north past the orchard. Beyond it the fields gave way to a
        silver haze the colour of old coins, and Marlowe slowed the cart,
        because a thing like that asks to be looked at.
      </p>
      <p
        style={{
          marginTop: 16,
          color: "rgba(34,29,23,.26)",
          fontSize: "clamp(19px,2.1vw,23px)",
          lineHeight: 1.66,
        }}
      >
        He had never been a man for landscapes. That was the part that worried
        him.
        <span className="caret" style={{ background: "var(--ink)" }} />
      </p>
      <hr className="rule-soft" style={{ margin: "26px 0 16px" }} />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          color: "var(--faint)",
          fontFamily: "var(--sans)",
          fontSize: 13,
        }}
      >
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          <span style={{ color: "var(--oxblood)", fontWeight: 600 }}>
            {wordCount.toLocaleString()}
          </span>{" "}
          / 1,500 words today
        </span>
        <span>~ 4 min read</span>
      </div>
      <div
        style={{
          height: 3,
          marginTop: 10,
          overflow: "hidden",
          borderRadius: 3,
          background: "var(--rule-soft)",
        }}
      >
        <div
          style={{
            width: `${Math.min(100, (wordCount / 1500) * 100)}%`,
            height: "100%",
            borderRadius: 3,
            background: "var(--oxblood)",
            transition: "width .3s ease",
          }}
        />
      </div>
    </div>
  );
}

function Page() {
  return (
    <section id="page" style={{ padding: "clamp(40px,6vw,72px) 0" }}>
      <div className="wrap">
        <div className="split">
          <div className="order-copy">
            <ChapterHead no="Chapter II" name="The Page" />
            <h2 className="h-sec" style={{ marginBottom: 22 }}>
              An editor that keeps
              <br />
              you in the story.
            </h2>
            <p className="prose" style={{ marginBottom: 18 }}>
              Rich text built for the long haul — typography you&apos;d happily
              read for hours, a margin wide enough to think in, and
              distraction-free tools.
            </p>
            <p className="prose" style={{ color: "var(--muted)" }}>
              Set a word goal for the session, dim the world down to a single
              warm column of text, and write until it's complete. The studio
              waits in the wings until you reach for it.
            </p>
          </div>
          <div className="order-plate">
            <FocusPlate />
          </div>
        </div>
      </div>
    </section>
  );
}

function Assistant() {
  const [stage, setStage] = useState<
    "idle" | "thinking" | "proposed" | "applied"
  >("idle");
  const startedRef = useRef(false);
  const ref = useInViewOnce(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    setStage("thinking");
    window.setTimeout(() => setStage("proposed"), 1500);
  });

  const ask = () => {
    setStage("thinking");
    window.setTimeout(() => setStage("proposed"), 1300);
  };
  const baseText =
    "Beyond it the fields gave way to a silver haze the colour of old coins.";
  const newText =
    "Beyond it the fields gave way to a silver haze the colour of tarnished coin, flat and patient as the sea.";

  return (
    <section
      id="assistant"
      ref={ref}
      style={{
        position: "relative",
        padding: "clamp(70px,9vw,124px) 0",
        background:
          "linear-gradient(180deg, transparent, rgba(138,59,46,.05) 22%, rgba(138,59,46,.05) 78%, transparent)",
      }}
    >
      <div className="wrap">
        <div style={{ maxWidth: 760, margin: "0 auto", textAlign: "center" }}>
          <ChapterHead no="Chapter III" name="The Assistant" align="center" />
          <h2 className="h-sec" style={{ marginBottom: 22 }}>
            A quiet assistant.
            <br />
            Only when you ask.
          </h2>
          <p className="lede" style={{ maxWidth: 640, margin: "0 auto 10px" }}>
            When you want a second set of eyes, Penzr can help you find a lost
            thread, look across your notes, or offer a new shape for a tired
            sentence. It never takes over the page. Every suggestion arrives as
            a preview you can read, change, ignore, or approve. The voice stays
            yours.
          </p>
        </div>

        <div style={{ maxWidth: 880, margin: "48px auto 0" }}>
          <div className="plate" style={{ padding: 0, overflow: "hidden" }}>
            <div
              style={{
                padding: "clamp(26px,3.4vw,40px) clamp(26px,4vw,56px) 30px",
              }}
            >
              <div
                style={{
                  marginBottom: 20,
                  color: "var(--faint)",
                  fontFamily: "var(--sans)",
                  fontSize: 11.5,
                  letterSpacing: ".2em",
                  textTransform: "uppercase",
                }}
              >
                Manuscript · Chapter Two
              </div>
              <p
                style={{
                  margin: 0,
                  fontSize: "clamp(20px,2.2vw,24px)",
                  lineHeight: 1.62,
                }}
              >
                The road bent north past the orchard.{" "}
                {stage === "applied" ? (
                  <span
                    style={{
                      borderRadius: 2,
                      background: "rgba(60,110,74,.14)",
                      boxShadow: "0 0 0 3px rgba(60,110,74,.14)",
                      transition: "background .5s",
                    }}
                  >
                    {newText}
                  </span>
                ) : (
                  <span
                    style={{
                      padding: "1px 0",
                      borderBottom:
                        stage === "proposed"
                          ? "2px solid var(--oxblood)"
                          : "2px solid transparent",
                      borderRadius: 1,
                      background:
                        stage === "proposed"
                          ? "rgba(138,59,46,.10)"
                          : "transparent",
                      transition: "all .45s ease",
                    }}
                  >
                    {baseText}
                  </span>
                )}
              </p>
            </div>

            <hr className="rule-soft" style={{ margin: 0 }} />
            <div
              style={{
                padding:
                  "clamp(20px,2.6vw,28px) clamp(26px,4vw,56px) clamp(24px,3vw,32px)",
                background: "rgba(34,29,23,.025)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  flexWrap: "wrap",
                  marginBottom: 18,
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    color: "var(--oxblood)",
                    fontFamily: "var(--sans)",
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: ".12em",
                    textTransform: "uppercase",
                  }}
                >
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: 7,
                      background: "var(--oxblood)",
                      opacity: stage === "thinking" ? 1 : 0.55,
                      animation:
                        stage === "thinking"
                          ? "glow 1s ease-in-out infinite"
                          : "none",
                    }}
                  />
                  You asked Penzr
                </span>
                <span
                  style={{
                    color: "var(--muted)",
                    fontSize: 17,
                    fontStyle: "italic",
                  }}
                >
                  &quot;Make that last line sharper — something about the
                  sea.&quot;
                </span>
              </div>

              {stage === "idle" && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 16,
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      color: "var(--faint)",
                      fontSize: 17,
                      fontStyle: "italic",
                    }}
                  >
                    Penzr is ready when you are. Nothing runs on its own.
                  </span>
                  <button className="btn btn-ox btn-sm" onClick={ask}>
                    Ask for a suggestion
                  </button>
                </div>
              )}

              {stage === "thinking" && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    color: "var(--muted)",
                    fontSize: 17,
                    fontStyle: "italic",
                  }}
                >
                  <Dots /> Reading the chapter and your notes…
                </div>
              )}

              {(stage === "proposed" || stage === "applied") && (
                <div
                  style={{
                    overflow: "hidden",
                    border: "1px solid var(--oxblood)",
                    borderRadius: 4,
                    background: "var(--paper)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "10px 16px",
                      background: "rgba(138,59,46,.07)",
                      color: "var(--oxblood)",
                      fontFamily: "var(--sans)",
                      fontSize: 11.5,
                      fontWeight: 600,
                      letterSpacing: ".14em",
                      textTransform: "uppercase",
                    }}
                  >
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: 6,
                        background: "var(--oxblood)",
                      }}
                    />
                    {stage === "applied"
                      ? "Applied by you"
                      : "Suggested rewrite · preview only"}
                  </div>
                  <div
                    style={{
                      padding: "clamp(14px,2vw,18px) clamp(16px,2.4vw,22px)",
                    }}
                  >
                    <p
                      style={{
                        margin: 0,
                        fontSize: "clamp(18px,2vw,21px)",
                        lineHeight: 1.56,
                      }}
                    >
                      &quot;…a silver haze the colour of{" "}
                      <span className="oxblood">
                        tarnished coin, flat and patient as the sea.
                      </span>
                      &quot;
                    </p>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      flexWrap: "wrap",
                      padding: "0 clamp(16px,2.4vw,22px) 18px",
                    }}
                  >
                    {stage === "proposed" ? (
                      <>
                        <button
                          className="btn btn-ox btn-sm"
                          onClick={() => setStage("applied")}
                        >
                          <IconCheck size={16} /> Approve & apply
                        </button>
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => setStage("idle")}
                        >
                          Dismiss
                        </button>
                        <span
                          style={{
                            marginLeft: "auto",
                            color: "var(--faint)",
                            fontSize: 15,
                            fontStyle: "italic",
                          }}
                        >
                          Edit it first, or send it back. Your call.
                        </span>
                      </>
                    ) : (
                      <>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            color: "var(--saved)",
                            fontFamily: "var(--sans)",
                            fontSize: 14,
                            fontWeight: 600,
                          }}
                        >
                          <IconCheck size={16} /> Added to your manuscript
                        </span>
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => setStage("idle")}
                          style={{ marginLeft: "auto" }}
                        >
                          Run the demo again
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          <p
            style={{
              marginTop: 34,
              color: "var(--muted)",
              fontSize: "clamp(20px,2.4vw,26px)",
              fontStyle: "italic",
              textAlign: "center",
            }}
          >
            Nothing reaches your page without your yes.
          </p>
        </div>
      </div>
    </section>
  );
}

function Dots() {
  return (
    <span style={{ display: "inline-flex", gap: 4 }}>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          style={{
            width: 6,
            height: 6,
            borderRadius: 6,
            background: "var(--oxblood)",
            animation: "glow 1.1s ease-in-out infinite",
            animationDelay: `${i * 0.18}s`,
          }}
        />
      ))}
    </span>
  );
}

function CollabPlate() {
  return (
    <div
      className="plate"
      style={{
        maxWidth: 520,
        padding: "clamp(22px,3vw,32px) clamp(24px,3vw,38px)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 22,
        }}
      >
        <span
          style={{
            color: "var(--faint)",
            fontFamily: "var(--sans)",
            fontSize: 11.5,
            letterSpacing: ".2em",
            textTransform: "uppercase",
          }}
        >
          Chapter Two · live
        </span>
        <span style={{ display: "flex", alignItems: "center" }}>
          <span style={{ display: "flex", marginRight: 8 }}>
            <Avatar initials="MR" tint="#8a3b2e" />
            <span style={{ marginLeft: -8 }}>
              <Avatar initials="EJ" tint="#3c6e4a" />
            </span>
            <span style={{ marginLeft: -8 }}>
              <Avatar initials="Aa" tint="#7a6cae" />
            </span>
          </span>
          <span
            style={{
              color: "var(--muted)",
              fontFamily: "var(--sans)",
              fontSize: 12.5,
            }}
          >
            3 here
          </span>
        </span>
      </div>
      <p style={{ margin: 0, fontSize: 19, lineHeight: 1.6 }}>
        He had never been a man for landscapes.
        <span
          style={{
            position: "relative",
            display: "inline-block",
            width: 2,
            height: "1.05em",
            margin: "0 1px",
            background: "#3c6e4a",
            verticalAlign: "-3px",
          }}
        >
          <span
            style={{
              position: "absolute",
              top: -16,
              left: 0,
              padding: "1px 5px",
              borderRadius: 2,
              background: "#3c6e4a",
              color: "#fff",
              fontFamily: "var(--sans)",
              fontSize: 10,
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            Elena
          </span>
        </span>{" "}
        <span style={{ borderRadius: 1, background: "rgba(124,108,174,.16)" }}>
          That was the part that worried him.
        </span>
      </p>
      <div
        style={{
          overflow: "hidden",
          marginTop: 22,
          border: "1px solid var(--rule)",
          borderRadius: 4,
        }}
      >
        <Comment initials="Aa" tint="#7a6cae" name="Aanya" time="2:14 pm">
          Love this turn — but does he worry, or is he just curious? Worth a
          beat here.
        </Comment>
        <hr className="rule-soft" style={{ margin: 0 }} />
        <Comment initials="MR" tint="#8a3b2e" name="Marlow R." time="2:16 pm">
          Worry. It pays off in ch. 5. Keeping it.
        </Comment>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "11px 15px",
            borderTop: "1px solid var(--rule-soft)",
            color: "var(--faint)",
            fontFamily: "var(--sans)",
            fontSize: 13.5,
          }}
        >
          <span style={{ flex: 1 }}>Reply…</span>
          <span className="btn btn-sm btn-ink">Send</span>
        </div>
      </div>
    </div>
  );
}

function Comment({
  initials,
  tint,
  name,
  time,
  children,
}: {
  initials: string;
  tint: string;
  name: string;
  time: string;
  children: ReactNode;
}) {
  return (
    <div style={{ display: "flex", gap: 10, padding: "13px 15px" }}>
      <Avatar initials={initials} tint={tint} size={26} />
      <div>
        <div
          style={{ fontFamily: "var(--sans)", fontSize: 13, fontWeight: 600 }}
        >
          {name}
          <span
            style={{ marginLeft: 8, color: "var(--faint)", fontWeight: 400 }}
          >
            {time}
          </span>
        </div>
        <div style={{ marginTop: 2, color: "var(--ink-soft)", fontSize: 16 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function Company() {
  return (
    <section id="company" style={{ padding: "clamp(64px,9vw,118px) 0" }}>
      <div className="wrap">
        <div className="split">
          <div className="order-plate">
            <CollabPlate />
          </div>
          <div className="order-copy">
            <ChapterHead no="Chapter IV" name="Company" />
            <h2 className="h-sec" style={{ marginBottom: 22 }}>
              Write together,
              <br />
              in the same room.
            </h2>
            <p className="prose dropcap" style={{ marginBottom: 18 }}>
              Invite a co-author, an editor, a trusted first reader. See who
              else is in the manuscript and watch their cursor move in real
              time. Leave a note in the margin and let the conversation happen
              exactly where the question lives — reply, resolve, move on.
            </p>
            <p className="prose" style={{ color: "var(--muted)" }}>
              No more emailing <em>draft_FINAL_v7.docx</em> into the void. One
              manuscript, everyone on the same page — literally.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Desk() {
  const items = [
    {
      icon: <IconSearch size={22} />,
      title: "Find the thread again",
      body: "Search across your scenes, notes, research, and revisions. Look for an exact phrase, or describe the moment you half-remember and let Penzr help you return to it.",
    },
    {
      icon: <IconHistory size={22} />,
      title: "Every draft has a way back",
      body: "Penzr keeps your writing history as you work. Compare versions, see what changed, and restore an earlier draft when a better path turns out to be the one you left behind.",
    },
    {
      icon: <IconShield size={22} />,
      title: "Your work stays yours",
      body: "Download a complete backup, or send one to your own cloud. Restore the whole project when you need to. Your manuscript, notes, and revisions stay safe and portable.",
    },
    {
      icon: <IconExport size={22} />,
      title: "Share a clean draft",
      body: "Export a polished DOCX with headings, emphasis, and structure intact. Send it to an agent, a publisher, an editor, or your writing group without rebuilding the manuscript somewhere else.",
    },
  ];

  return (
    <section style={{ padding: "clamp(56px,8vw,104px) 0" }}>
      <div className="wrap">
        <div style={{ maxWidth: 620, marginBottom: "clamp(40px,5vw,60px)" }}>
          <ChapterHead no="Appendix" name="The rest of the desk" />
          <h2 className="h-sec">
            Everything a story
            <br />
            quietly needs.
          </h2>
          <p
            className="prose"
            style={{ maxWidth: 540, marginTop: 16, color: "var(--muted)" }}
          >
            The practical things that help a draft keep moving — saved, backed
            up, searchable, and ready to share when the next reader is waiting.
          </p>
        </div>
        <div className="desk-grid">
          {items.map((item) => (
            <article key={item.title} className="desk-card">
              <div style={{ marginBottom: 16, color: "var(--oxblood)" }}>
                {item.icon}
              </div>
              <h3 style={{ marginBottom: 10, fontSize: 25, fontWeight: 500 }}>
                {item.title}
              </h3>
              <p
                className="prose"
                style={{ margin: 0, color: "var(--muted)", fontSize: 18 }}
              >
                {item.body}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  const [annual, setAnnual] = useState(true);
  const proPrice = annual ? "10" : "12";

  return (
    <section id="pricing" style={{ padding: "clamp(64px,9vw,118px) 0" }}>
      <div className="wrap">
        <div
          style={{
            maxWidth: 640,
            margin: "0 auto clamp(36px,4.5vw,52px)",
            textAlign: "center",
          }}
        >
          <ChapterHead no="Chapter V" name="The Terms" align="center" />
          <h2 className="h-sec" style={{ marginBottom: 18 }}>
            The right plan for <br /> your next story.
          </h2>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 14,
              marginTop: 6,
              fontFamily: "var(--sans)",
              fontSize: 14.5,
            }}
          >
            <button
              onClick={() => setAnnual(false)}
              style={{
                border: 0,
                background: "transparent",
                color: annual ? "var(--faint)" : "var(--ink)",
                cursor: "pointer",
                fontFamily: "var(--sans)",
                fontSize: 14.5,
                fontWeight: annual ? 400 : 700,
              }}
            >
              Monthly
            </button>
            <button
              onClick={() => setAnnual(!annual)}
              role="switch"
              aria-checked={annual}
              aria-label="Toggle annual billing"
              style={{
                position: "relative",
                width: 46,
                height: 26,
                border: 0,
                borderRadius: 26,
                background: annual ? "var(--oxblood)" : "var(--rule)",
                cursor: "pointer",
                transition: "background .25s",
              }}
            >
              <span
                style={{
                  position: "absolute",
                  top: 3,
                  left: annual ? 23 : 3,
                  width: 20,
                  height: 20,
                  borderRadius: 20,
                  background: "#fff",
                  boxShadow: "0 1px 3px rgba(0,0,0,.3)",
                  transition: "left .25s",
                }}
              />
            </button>
            <button
              onClick={() => setAnnual(true)}
              style={{
                border: 0,
                background: "transparent",
                color: annual ? "var(--ink)" : "var(--faint)",
                cursor: "pointer",
                fontFamily: "var(--sans)",
                fontSize: 14.5,
                fontWeight: annual ? 700 : 400,
              }}
            >
              Annual
            </button>
            <span
              style={{
                color: "var(--oxblood)",
                fontFamily: "var(--serif)",
                fontSize: 16,
                fontStyle: "italic",
              }}
            >
              — save $24 a year
            </span>
          </div>
        </div>

        <div className="price-grid">
          <article className="price-card">
            <div
              style={{
                color: "var(--faint)",
                fontFamily: "var(--sans)",
                fontSize: 12.5,
                fontWeight: 600,
                letterSpacing: ".2em",
                textTransform: "uppercase",
              }}
            >
              Free
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 6,
                margin: "14px 0 6px",
              }}
            >
              <span style={{ fontSize: 56, fontWeight: 500, lineHeight: 1 }}>
                $0
              </span>
              <span style={{ color: "var(--faint)", fontSize: 18 }}>
                forever
              </span>
            </div>
            <p
              style={{
                margin: "0 0 24px",
                color: "var(--muted)",
                fontSize: 18,
              }}
            >
              Your first book, in full. The complete studio — binder, editor,
              version history, full backups, and unlimited exports. No time
              limit, no asterisk.
            </p>
            <a
              className="btn btn-ink"
              href={APP_URL}
              style={{ width: "100%", justifyContent: "center" }}
            >
              Start writing
            </a>
            <hr className="rule-soft" style={{ margin: "26px 0 22px" }} />
            <Feat>One project</Feat>
            <Feat>The full editor & binder</Feat>
            <Feat>Version history & one-click restore</Feat>
            <Feat>Full project backups</Feat>
            <Feat>Publish to Word (DOCX)</Feat>
          </article>

          <article className="price-card price-card--pro">
            <div
              style={{
                position: "absolute",
                top: -13,
                left: 32,
                padding: "5px 12px",
                borderRadius: 2,
                background: "var(--oxblood)",
                color: "#f7efe2",
                fontFamily: "var(--sans)",
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: ".16em",
                textTransform: "uppercase",
                whiteSpace: "nowrap",
              }}
            >
              For the professional writer
            </div>
            <div
              style={{
                color: "var(--oxblood)",
                fontFamily: "var(--sans)",
                fontSize: 12.5,
                fontWeight: 600,
                letterSpacing: ".2em",
                textTransform: "uppercase",
              }}
            >
              Pro
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 6,
                margin: "14px 0 6px",
              }}
            >
              <span style={{ fontSize: 56, fontWeight: 500, lineHeight: 1 }}>
                ${proPrice}
              </span>
              <span style={{ color: "var(--faint)", fontSize: 18 }}>
                / month
              </span>
            </div>
            <p
              style={{
                margin: "0 0 24px",
                color: "var(--muted)",
                fontSize: 18,
              }}
            >
              {annual ? (
                <>
                  Billed annually at{" "}
                  <strong style={{ color: "var(--ink)" }}>$120/year</strong>.
                  For professional writers that require the full power of Penzr.
                </>
              ) : (
                <>
                  Billed monthly, cancel anytime. Switch to annual and it&apos;s
                  $10/mo.
                </>
              )}
            </p>
            <a
              className="btn btn-ox"
              href={PRO_INTENT_URL}
              style={{ width: "100%", justifyContent: "center" }}
            >
              Go Pro
            </a>
            <hr className="rule-soft" style={{ margin: "26px 0 22px" }} />
            <Feat strong>Everything in Free, plus —</Feat>
            <Feat>Unlimited projects</Feat>
            <Feat>AI writing assistant</Feat>
            <Feat>Advanced publishing (PDF, Kindle, ePub)</Feat>
            <Feat>Automated project backups</Feat>
          </article>
        </div>
      </div>
    </section>
  );
}

function Feat({
  children,
  muted = false,
  strong = false,
}: TextProps & { muted?: boolean; strong?: boolean }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 11,
        marginBottom: 12,
        color: muted ? "var(--faint)" : "var(--ink-soft)",
        fontSize: 17.5,
        fontWeight: strong ? 600 : 400,
      }}
    >
      <span
        style={{
          flex: "none",
          marginTop: 2,
          color: muted ? "var(--faint)" : "var(--oxblood)",
        }}
      >
        {muted ? (
          <svg
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M6 12h12" />
          </svg>
        ) : (
          <IconCheck size={17} />
        )}
      </span>
      <span>{children}</span>
    </div>
  );
}

function Closing() {
  return (
    <section
      style={{ position: "relative", padding: "clamp(70px,10vw,130px) 0" }}
    >
      <div className="wrap" style={{ textAlign: "center" }}>
        <hr className="small-rule" style={{ margin: "0 auto 30px" }} />
        <h2
          className="display"
          style={{ marginBottom: 24, fontSize: "clamp(40px,6.5vw,76px)" }}
        >
          Your first page
          <br />
          is waiting.
        </h2>
        <p className="lede" style={{ maxWidth: 540, margin: "0 auto 36px" }}>
          Open Penzr, name the book, and write the first true sentence.
          Everything else — the binder, the drafts, the safekeeping — is already
          here.
        </p>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 20,
            flexWrap: "wrap",
          }}
        >
          <a className="btn btn-ink" href={APP_URL}>
            Start writing — it&apos;s free <IconArrow size={17} />
          </a>
          <a className="btn btn-ghost" href="#pricing">
            See pricing
          </a>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer
      style={{
        padding: "54px 0 60px",
        borderTop: "1px solid var(--rule)",
        background: "rgba(34,29,23,.02)",
      }}
    >
      <div className="wrap">
        <div className="footer-grid">
          <div style={{ maxWidth: 320 }}>
            <Wordmark />
            <p
              style={{
                marginTop: 16,
                color: "var(--muted)",
                fontSize: 17,
                lineHeight: 1.55,
              }}
            >
              A calm home for the story you are trying to finish. Built for the
              distance, and for keeping you in charge of every word.
            </p>
          </div>
          <FooterCol
            title="Studio"
            links={[
              ["Features", "#binder"],
              ["Assistant", "#assistant"],
              ["Pricing", "#pricing"],
              ["Sign in", APP_URL],
            ]}
          />
          <FooterCol
            title="Company"
            links={[
              ["Privacy Policy", "/privacy"],
              ["Terms of Service", "/terms"],
            ]}
          />
          <div>
            <div className="kicker" style={{ marginBottom: 14 }}>
              Begin
            </div>
            <a className="btn btn-ink btn-sm" href={APP_URL}>
              Start writing
            </a>
          </div>
        </div>
        <hr className="rule-soft" style={{ margin: "40px 0 24px" }} />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
            color: "var(--faint)",
            fontFamily: "var(--sans)",
            fontSize: 13.5,
          }}
        >
          <span>
            Penzr by 2toad · © 2026. Web-based — no native app required.
          </span>
          <span
            style={{
              fontFamily: "var(--serif)",
              fontSize: 16,
              fontStyle: "italic",
            }}
          >
            Made for people who finish.
          </span>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({
  title,
  links,
}: {
  title: string;
  links: Array<[string, string]>;
}) {
  return (
    <div>
      <div className="kicker" style={{ marginBottom: 14 }}>
        {title}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {links.map(([label, href]) => (
          <a
            key={label}
            className="link-ul"
            href={href}
            style={{ fontSize: 16.5 }}
          >
            {label}
          </a>
        ))}
      </div>
    </div>
  );
}

function MarketingPage() {
  return (
    <>
      <Nav />
      <main>
        <Hero />
        <Binder />
        <Page />
        <Assistant />
        <Company />
        <Desk />
        <Pricing />
        <Closing />
      </main>
      <Footer />
    </>
  );
}

function LegalLayout({
  kicker,
  title,
  meta,
  children,
}: {
  kicker: string;
  title: string;
  meta: string;
  children: ReactNode;
}) {
  return (
    <>
      <header style={{ borderBottom: "1px solid var(--rule)" }}>
        <div
          className="wrap doc-wrap"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingTop: 22,
            paddingBottom: 22,
          }}
        >
          <Wordmark size={21} />
          <a className="nav-link" href="/">
            Back to Penzr
          </a>
        </div>
      </header>
      <main className="wrap doc-wrap" style={{ paddingTop: 54 }}>
        <div className="kicker">{kicker}</div>
        <h1
          style={{
            marginTop: 14,
            fontSize: "clamp(40px,6vw,60px)",
            lineHeight: 1.04,
          }}
        >
          {title}
        </h1>
        <p
          style={{
            marginTop: 18,
            color: "var(--faint)",
            fontFamily: "var(--sans)",
            fontSize: 13.5,
            letterSpacing: ".02em",
          }}
        >
          {meta}
        </p>
        <hr className="rule" style={{ margin: "34px 0" }} />
        <div className="legal-copy">{children}</div>
      </main>
      <footer
        style={{
          marginTop: 60,
          padding: "28px 0 60px",
          borderTop: "1px solid var(--rule)",
        }}
      >
        <div
          className="wrap doc-wrap"
          style={{
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 10,
            color: "var(--faint)",
            fontFamily: "var(--sans)",
            fontSize: 13.5,
          }}
        >
          <span>Penzr by 2toad · © 2026.</span>
          <span>
            <a className="link-ul" href="/privacy">
              Privacy Policy
            </a>{" "}
            ·{" "}
            <a className="link-ul" href="/terms">
              Terms of Service
            </a>{" "}
            ·{" "}
            <a className="link-ul" href="/">
              Home
            </a>
          </span>
        </div>
      </footer>
    </>
  );
}

function LegalLead({ children }: TextProps) {
  return (
    <p style={{ margin: "0 0 16px", color: "var(--ink-soft)", fontSize: 22 }}>
      {children}
    </p>
  );
}

function LegalSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section>
      <h2 style={{ margin: "42px 0 12px", fontSize: 27, fontWeight: 600 }}>
        {title}
      </h2>
      {children}
    </section>
  );
}

function LegalP({ children }: TextProps) {
  return (
    <p style={{ margin: "0 0 16px", color: "var(--ink-soft)" }}>{children}</p>
  );
}

function LegalList({ children }: TextProps) {
  return (
    <ul
      style={{ margin: "0 0 16px", paddingLeft: 22, color: "var(--ink-soft)" }}
    >
      {children}
    </ul>
  );
}

function PrivacyPage() {
  return (
    <LegalLayout
      kicker="The fine print, plainly"
      title="Privacy Policy"
      meta="Last updated 2 June 2026 · Penzr is operated by 2toad."
    >
      <LegalLead>
        Your manuscript is yours. This page explains what we collect, why, and
        the few promises we hold ourselves to. We&apos;ve tried to write it the
        way we&apos;d want it written for us — short, specific, no theatre.
      </LegalLead>
      <LegalSection title="What we collect">
        <LegalList>
          <li>
            <strong>Your account.</strong> An email address and an encrypted
            password, so you can sign in and we can reach you about your
            account.
          </li>
          <li>
            <strong>Your work.</strong> The text, structure, research, comments
            and version history you create. We store it so the studio works
            across your devices and so your drafts are safe.
          </li>
          <li>
            <strong>Basic usage.</strong> Aggregate, mostly anonymous signals —
            which features are used, when errors occur — so we can fix and
            improve Penzr.
          </li>
          <li>
            <strong>Billing.</strong> If you subscribe to Pro, payments are
            handled by our payment processor. We never see or store your full
            card details.
          </li>
        </LegalList>
      </LegalSection>
      <LegalSection title="Your writing and the AI">
        <LegalP>
          When you ask Penzr&apos;s assistant for a suggestion, the relevant
          text is sent to our AI provider to generate that response, and
          returned to you as a preview. We do not use your manuscript to train
          AI models. The assistant never writes into your manuscript on its own
          — every suggestion is applied only when you explicitly approve it.
        </LegalP>
      </LegalSection>
      <LegalSection title="Backups and your own cloud">
        <LegalP>
          You can download complete backups of any project, or connect your own
          cloud storage and have backups forwarded there. When you connect an
          outside service, you authorise that connection and can revoke it at
          any time.
        </LegalP>
      </LegalSection>
      <LegalSection title="What we don't do">
        <LegalList>
          <li>We don&apos;t sell your personal information.</li>
          <li>
            We don&apos;t use the contents of your manuscripts for advertising.
          </li>
          <li>We don&apos;t train AI models on your work.</li>
        </LegalList>
      </LegalSection>
      <LegalSection title="Your choices">
        <LegalP>
          You can export your projects, delete individual projects, or close
          your account and have its contents removed. For questions or requests,
          write to us at{" "}
          <a className="link-ul" href="mailto:privacy@penzr.com">
            privacy@penzr.com
          </a>
          .
        </LegalP>
      </LegalSection>
    </LegalLayout>
  );
}

function TermsPage() {
  return (
    <LegalLayout
      kicker="The agreement, plainly"
      title="Terms of Service"
      meta="Last updated 2 June 2026 · An agreement between you and 2toad."
    >
      <LegalLead>
        By using Penzr you agree to these terms. We&apos;ve kept them as plain
        as we can. The short version: write what you like, own what you write,
        and use the studio in good faith.
      </LegalLead>
      <LegalSection title="Your account">
        <LegalP>
          You&apos;re responsible for your account and for keeping your password
          safe. Penzr is for individuals and teams aged 13 and over. One person,
          one account; you may invite collaborators into specific projects.
        </LegalP>
      </LegalSection>
      <LegalSection title="Your content is yours">
        <LegalP>
          You retain all rights to the work you create in Penzr. We claim no
          ownership of your manuscripts. You grant us only the limited
          permission needed to store, display, back up, and process your work so
          the service can function.
        </LegalP>
      </LegalSection>
      <LegalSection title="The plans">
        <LegalList>
          <li>
            <strong>Free.</strong> One project with the full editor, binder,
            version history, manual backups, and DOCX export, at no cost and
            with no time limit. The Free plan does not include AI writing.
          </li>
          <li>
            <strong>Pro.</strong> $12 per month, or $10 per month billed
            annually ($120 per year). Pro adds unlimited projects, AI writing of
            200,000 tokens per month, automatic backups, and your-own-cloud
            backup forwarding.
          </li>
          <li>
            <strong>Top-ups.</strong> If you use your monthly AI allowance, you
            may add a one-time 200,000-token top-up for $5.
          </li>
        </LegalList>
      </LegalSection>
      <LegalSection title="Billing and cancellation">
        <LegalP>
          Paid plans renew automatically until cancelled. You can cancel at any
          time and keep Pro features through the end of the period you&apos;ve
          paid for; after that your account returns to the Free plan.
        </LegalP>
      </LegalSection>
      <LegalSection title="Acceptable use">
        <LegalP>
          Don&apos;t use Penzr to break the law, to infringe others&apos;
          rights, or to disrupt the service for other writers. Don&apos;t
          attempt to reverse-engineer or abuse the platform.
        </LegalP>
      </LegalSection>
      <LegalSection title="Your data is portable">
        <LegalP>
          You can export complete backups of your projects at any time and
          forward them to your own cloud storage. You are never locked in.
        </LegalP>
      </LegalSection>
    </LegalLayout>
  );
}

export default function App() {
  const path =
    typeof window === "undefined"
      ? ""
      : window.location.pathname.replace(/\/$/, "");

  if (path === "/privacy") return <PrivacyPage />;
  if (path === "/terms") return <TermsPage />;
  return <MarketingPage />;
}
